import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PriorityBadge, ElapsedTime } from "../components/badges";
import { DashboardSummary } from "../components/DashboardSummary";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [filters, setFilters] = useState({ status: "", priority: "", technician_id: "", search: "" });

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => v && (params[k] = v));
    const { data } = await api.get("/orders", { params });
    setOrders(data);
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, filters.search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, filters.search]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/users/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
    }
  }, [user]);

  const hasFilters = Object.values(filters).some(Boolean);
  const clearFilters = () => setFilters({ status: "", priority: "", technician_id: "", search: "" });

  return (
    <div className="space-y-4" data-testid="dashboard">
      <DashboardSummary />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          {orders ? `${orders.length} orden${orders.length === 1 ? "" : "es"} encontrada${orders.length === 1 ? "" : "s"}` : "Cargando órdenes..."}
        </p>
        <Button
          onClick={() => navigate("/orders/new")}
          data-testid="new-order-button"
          className="gap-2 bg-primary hover:bg-[#0353E9] transition-colors duration-200"
        >
          <Plus className="h-4 w-4" />
          Nueva Orden
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar por N° de orden, cliente o equipo..."
            className="pl-9"
            data-testid="filter-search"
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
        </div>
        <Select
          value={filters.status || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}
        >
          <SelectTrigger className="w-[150px]" data-testid="filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="pendiente">Pendiente</SelectItem>
            <SelectItem value="en_progreso">En progreso</SelectItem>
            <SelectItem value="completado">Completado</SelectItem>
            <SelectItem value="escalado">Escalado</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.priority || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, priority: v === "all" ? "" : v }))}
        >
          <SelectTrigger className="w-[140px]" data-testid="filter-priority">
            <SelectValue placeholder="Prioridad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
            <SelectItem value="media">Media</SelectItem>
            <SelectItem value="baja">Baja</SelectItem>
          </SelectContent>
        </Select>
        {user?.role === "admin" && (
          <Select
            value={filters.technician_id || "all"}
            onValueChange={(v) => setFilters((f) => ({ ...f, technician_id: v === "all" ? "" : v }))}
          >
            <SelectTrigger className="w-[170px]" data-testid="filter-technician">
              <SelectValue placeholder="Técnico" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los técnicos</SelectItem>
              {technicians.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-button" className="gap-1 text-slate-500">
            <X className="h-4 w-4" /> Limpiar
          </Button>
        )}
      </div>

      {orders === null ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="orders-empty">
          <p className="font-heading text-lg font-semibold text-slate-700">No hay órdenes para mostrar</p>
          <p className="mt-1 text-sm text-slate-500">Cree una nueva orden o ajuste los filtros.</p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-slate-200 bg-white md:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">N° Orden</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Técnico</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Prioridad</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Estado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Creada</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Tiempo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow
                    key={o.id}
                    data-testid={`order-row-${o.order_number}`}
                    className="cursor-pointer transition-colors duration-200 hover:bg-slate-50"
                    onClick={() => navigate(`/orders/${o.id}`)}
                  >
                    <TableCell className="font-mono text-sm font-semibold text-primary">{o.order_number}</TableCell>
                    <TableCell className="text-sm font-medium text-slate-900">{o.client_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{o.technician_name}</TableCell>
                    <TableCell><PriorityBadge priority={o.priority} /></TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-sm text-slate-500">{(o.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell>
                      {o.status !== "completado" ? (
                        <ElapsedTime since={o.created_at} high={o.priority === "alta"} testId={`elapsed-${o.order_number}`} />
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-3 md:hidden">
            {orders.map((o) => (
              <Link
                key={o.id}
                to={`/orders/${o.id}`}
                data-testid={`order-card-${o.order_number}`}
                className="block rounded-lg border border-slate-200 bg-white p-4 transition-transform duration-200 hover:-translate-y-0.5"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm font-bold text-primary">{o.order_number}</span>
                  <StatusBadge status={o.status} />
                </div>
                <p className="mt-2 text-sm font-semibold text-slate-900">{o.client_name}</p>
                <p className="text-xs text-slate-500">{o.technician_name} · {(o.created_at || "").slice(0, 10)}</p>
                <div className="mt-3 flex items-center gap-2">
                  <PriorityBadge priority={o.priority} />
                  {o.status !== "completado" && (
                    <ElapsedTime since={o.created_at} high={o.priority === "alta"} />
                  )}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
